/**
 * Drill cache helpers for stale-while-revalidate catalog refresh.
 */

import {
  findTreeNodeById,
  filterProductsForPosGridCategory,
  toProductTileRef
} from '@/utils/gridLayout'
import { normalizePosProductRecord, productInCategory } from '@/utils/posProduct'

/**
 * Product slot ref with display fields for drill cache (avoids store lookup on paint).
 */
export function toProductDrillSlotRef (product) {
  const normalized = normalizePosProductRecord(product)
  if (!normalized) return null
  return {
    type: 'product',
    refId: normalized.id,
    name: normalized.name,
    price: normalized.price,
    barcode: normalized.barcode ?? null,
    ...(typeof normalized.has_pos_modifiers === 'boolean'
      ? { has_pos_modifiers: normalized.has_pos_modifiers }
      : {})
  }
}

/**
 * Subcategory tiles for a category drill-down (sorted by sort_order).
 * @param {number|string} categoryId
 * @param {Array<object>} categoryTree
 */
export function buildDrillSubcategoriesFromTree (categoryId, categoryTree) {
  const node = findTreeNodeById(categoryTree, categoryId)
  return (node?.children || [])
    .slice()
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map(c => ({
      type: 'category',
      refId: c.id,
      name: c.name,
      color: c.color,
      image_url: c.image_url
    }))
}

/**
 * Build one drill cache entry from the local catalog index (no network).
 * @param {number|string} categoryId
 * @param {Array<object>} categoryTree
 * @param {Array<object>} products
 * @param {(id: number) => object|null} getCategoryById
 */
export function buildDrillCacheEntryFromCatalog (categoryId, categoryTree, products, getCategoryById) {
  const categories = buildDrillSubcategoriesFromTree(categoryId, categoryTree)
  const sorted = filterProductsForPosGridCategory(categoryId, products, getCategoryById)
    .slice()
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
  const productSlots = sorted
    .map(p => toProductDrillSlotRef(p) ?? toProductTileRef(p))
    .filter(Boolean)
  const productTotal = productSlots.length
  const localFullySeeded = productTotal > 0 && productTotal <= 50

  return {
    categories,
    productSlots,
    productTotal,
    total: categories.length + productTotal,
    loadedApiPages: localFullySeeded ? { 1: true } : {},
    localSeeded: localFullySeeded,
    fetchedAt: Date.now()
  }
}

/**
 * @param {Array<object>} categoryTree
 * @param {Array<object>} products
 * @param {Array<number|string>} categoryIds
 * @param {(id: number) => object|null} getCategoryById
 * @returns {Record<string, object>}
 */
export function buildDrillCacheFromCatalog (categoryTree, products, categoryIds, getCategoryById) {
  const next = {}
  for (const rawId of categoryIds || []) {
    const categoryId = Number(rawId)
    if (!Number.isFinite(categoryId) || categoryId <= 0) continue
    next[String(categoryId)] = buildDrillCacheEntryFromCatalog(
      categoryId,
      categoryTree,
      products,
      getCategoryById
    )
  }
  return next
}

/**
 * Merge one API page of products into a drill cache entry.
 * @param {object} entry
 * @param {number} apiPage
 * @param {Array<object>} rows
 * @param {number} pageSize
 * @param {{ upsertProduct?: (raw: object) => void, productTotal?: number }} [options]
 */
export function mergeDrillApiPageIntoEntry (entry, apiPage, rows, pageSize = 50, options = {}) {
  const { upsertProduct, productTotal: apiProductTotal } = options
  const categories = entry?.categories || []
  const productTotal = Math.max(
    apiProductTotal ?? entry?.productTotal ?? 0,
    entry?.productSlots?.length ?? 0
  )

  const productSlots = productTotal > 0
    ? Array.from({ length: productTotal }, (_, i) => entry?.productSlots?.[i] ?? null)
    : [...(entry?.productSlots || [])]

  const startIndex = (apiPage - 1) * pageSize
  for (let i = 0; i < (rows || []).length; i++) {
    const slotIndex = startIndex + i
    if (slotIndex < 0) continue
    while (productSlots.length <= slotIndex) {
      productSlots.push(null)
    }
    const normalized = normalizePosProductRecord(rows[i])
    if (normalized && upsertProduct) {
      upsertProduct(rows[i])
    }
    productSlots[slotIndex] = toProductDrillSlotRef(normalized || rows[i]) ?? toProductTileRef(normalized || rows[i])
  }

  const resolvedTotal = Math.max(productTotal, productSlots.length)

  return {
    ...entry,
    categories,
    productSlots,
    productTotal: resolvedTotal,
    total: categories.length + resolvedTotal,
    loadedApiPages: {
      ...(entry?.loadedApiPages || {}),
      [apiPage]: true
    },
    fetchedAt: Date.now()
  }
}

/**
 * Keep drill cache structure and product refs for instant display, but clear
 * API page flags so background fetches pick up catalog changes.
 * @param {Record<string, object>|null|undefined} drillCache
 */
export function softInvalidateDrillCacheApiPages (drillCache) {
  if (!drillCache || typeof drillCache !== 'object') return {}
  const next = {}
  for (const [key, entry] of Object.entries(drillCache)) {
    if (!entry || typeof entry !== 'object') continue
    next[key] = {
      ...entry,
      loadedApiPages: {}
    }
  }
  return next
}

function sortedProductsForCategory (categoryId, products, getCategoryById) {
  const list = getCategoryById
    ? filterProductsForPosGridCategory(categoryId, products, getCategoryById)
    : (products || []).filter(p => productInCategory(p, categoryId))
  return list.slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
}

/**
 * Rebuild product slot refs from the products index while preserving categories
 * and slot count. Clears loaded API page flags for background refetch.
 * @param {Record<string, object>|null|undefined} drillCache
 * @param {Array<object>} products
 * @param {{ getCategoryById?: (id: number) => object|null, categoryTree?: Array<object>, categoryIds?: Array<number|string> }} [options]
 */
export function rewarmDrillCacheFromProducts (drillCache, products = [], options = {}) {
  const { getCategoryById, categoryTree, categoryIds } = options
  const invalidated = softInvalidateDrillCacheApiPages(drillCache)
  const next = { ...invalidated }

  const idsToProcess = new Set([
    ...Object.keys(invalidated),
    ...(categoryIds || []).map(String)
  ])

  for (const key of idsToProcess) {
    const categoryId = Number(key)
    if (!Number.isFinite(categoryId) || categoryId <= 0) continue

    const existing = invalidated[key]
    const categories = existing?.categories?.length
      ? existing.categories
      : (categoryTree ? buildDrillSubcategoriesFromTree(categoryId, categoryTree) : [])

    const sorted = sortedProductsForCategory(categoryId, products, getCategoryById)
    const catalogCount = sorted.length
    const slotCount = Math.max(existing?.productTotal ?? 0, catalogCount)

    if (slotCount <= 0 && !categories.length) {
      if (existing) {
        next[key] = { ...existing, loadedApiPages: {} }
      }
      continue
    }

    const productSlots = Array.from({ length: slotCount }, () => null)
    for (let i = 0; i < sorted.length && i < slotCount; i++) {
      productSlots[i] = toProductDrillSlotRef(sorted[i]) ?? toProductTileRef(sorted[i])
    }

    const localFullySeeded = catalogCount > 0 && catalogCount <= 50 && catalogCount >= slotCount

    next[key] = {
      categories,
      productSlots,
      productTotal: slotCount,
      total: categories.length + slotCount,
      loadedApiPages: localFullySeeded ? { 1: true } : {},
      localSeeded: localFullySeeded,
      fetchedAt: Date.now()
    }
  }

  return next
}
